/** @format */
import {
	AttributeOption,
	selectDisabledAttributes,
	selectSelectedColorAttribute
} from '@pdp/attributes/data-access';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, computed, inject, input, output, PLATFORM_ID } from '@angular/core';
import {
	HmfSelectableComponent,
	HmfSelectableOption,
	HmfSelectedOption
} from '@dsg-tech/homefield-angular';

import { AttributeLabelComponent } from '../attribute-label/attribute-label.component';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { Store } from '@ngrx/store';
import { selectProductPartNumber } from '@pdp/data-access/state';

@Component({
	selector: 'pdp-attributes-components-color-selection',
	templateUrl: './color-attributes.component.html',
	styleUrl: './color-attributes.component.scss',
	standalone: true,
	imports: [CommonModule, AttributeLabelComponent, HmfSelectableComponent]
})
export class ColorAttributesComponent {
	private readonly store = inject(Store);
	private readonly platformId = inject(PLATFORM_ID);

	selectDisabledAttributes$ = this.store.selectSignal(selectDisabledAttributes);
	selectProductPartNumber$ = this.store.selectSignal(selectProductPartNumber);

	readonly colorAttributeData = input.required<AttributeOption[]>();
	readonly alternativeInStockIsShowing = input.required<boolean>();
	readonly isColorBucket = input.required<boolean>();
	readonly bundlePartNumber = input<string>();
	readonly isMobile = input<boolean>();

	private readonly selectedColorAttributeFromState = this.store.selectSignal(
		selectSelectedColorAttribute
	);

	selectedAttributeOnClick = output<AttributeOption>();

	colorAttributesSelectableOptions = computed<HmfSelectableOption[]>(() =>
		this.colorAttributeData().map((colorAttribute, index) => {
			const unavailable =
				!this.selectDisabledAttributes$()[colorAttribute.name]?.includes(
					colorAttribute.value
				) ||
				(this.alternativeInStockIsShowing() && this.isSelectedColorSwatch(colorAttribute));
			const selected = this.isSelectedColorSwatch(colorAttribute);

			return {
				id: `${colorAttribute.value}_${index}`,
				value: this.getColorImageSrc(colorAttribute.value),
				imgAltText: colorAttribute.value,
				unavailable,
				selected
			};
		})
	);

	/**
	 * @description Emits an event when a color swatch is clicked
	 * and updates the current swatch to be selected.
	 **/
	selectedAttributeChange(ev: HmfSelectedOption[]): void {
		const selectedOption = ev[0];
		const indexOfUnderScore = `${selectedOption.id}`.indexOf('_');
		const colorFromEvent = `${selectedOption.id}`.substring(0, indexOfUnderScore);
		const attributeOption = this.colorAttributeData().find(
			(colorAttribute) => colorAttribute.value === colorFromEvent
		);
		this.selectedAttributeOnClick.emit(attributeOption);
	}

	/**
	 * @description Builds the color swatch image url and returns it.
	 **/
	getColorImageSrc(colorName: string): string {
		const imageParams = '?hei=80&wid=80&op_sharpen=1';
		let imagePath = DCSG_CONSTANTS.baseProductImagePath;
		const productPartNumber = this.selectProductPartNumber$() || this.bundlePartNumber();
		if (
			isPlatformBrowser(this.platformId) &&
			window.location.hostname.includes(`dksxchange`) &&
			!window.location.hostname.includes(`prod-`)
		) {
			imagePath = `${imagePath}GolfGalaxyTest/`;
		} else {
			imagePath = `${imagePath}dkscdn/`;
		}
		return `${imagePath}${productPartNumber}_${colorName.replace(
			/[^\w]/gi,
			'_'
		)}/${imageParams}`;
	}

	isSelectedColorSwatch(attributeOption: AttributeOption): boolean {
		return (
			this.selectedColorAttributeFromState()?.value?.toLowerCase() ===
			attributeOption?.value.toLowerCase()
		);
	}
}
